import {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { Property, useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import {
    ActionExecuteStartCondition,
    ChatSettingsEmojiAction, Form, Settings, useGetChatSettingsLazyQuery, Workflow, WorkflowTriggerType,
    WorkspaceEvent,
} from "../../../../../__generated__/api";
import { LoggingHandler } from "../../../../../contexts/logging/LoggingContext";

export type BaseProperties = {
    name: Property<string>;
    description: Property<string>;
};

export type ActionProperties = BaseProperties & {
    groups: Property<string[]>;
    startCondition: Property<ActionExecuteStartCondition>;
};

export type InboundWebhookProperties = BaseProperties & {
    groups: Property<string[]>;
};

export type EmojiProperties = BaseProperties & {
    emoji: Property<string>;
    groups: Property<string[]>;
    conversationSources: Property<{ label: string; value: string }[]>;
};

export type ChannelTagProperties = BaseProperties & {
    channelTags: Property<{ label: string; value: string }[]>;
};

export type EmailProperties = BaseProperties & {
    emails: Property<string[]>;
};

export type WorkspaceActionProperties = BaseProperties & {
    event: Property<string | null>;
};

export type PropertiesByTrigger<T extends WorkflowTriggerType> =
    T extends WorkflowTriggerType.Action ? ActionProperties
        : T extends WorkflowTriggerType.InboundWebhook ? InboundWebhookProperties
            : T extends WorkflowTriggerType.Emoji ? EmojiProperties
                : T extends WorkflowTriggerType.ChannelTag ? ChannelTagProperties
                    : T extends WorkflowTriggerType.Email ? EmailProperties
                        : T extends WorkflowTriggerType.WorkspaceAction ? WorkspaceActionProperties
                            : BaseProperties;

interface UseGeneralSettingsPropertiesProps<T extends WorkflowTriggerType> {
    triggerType: T;
    formData?: Form;
    settings?: Settings;
    workspaceEvents?: WorkspaceEvent[];
    workflow?: Workflow;
}

const EVERYONE_GROUP = "urn:fq:user-group:everyone";

export default function useGeneralSettingsProperties<T extends WorkflowTriggerType>({
    triggerType,
    formData,
    workspaceEvents,
    workflow,
}: UseGeneralSettingsPropertiesProps<T>) {
    const [getChatSettings] = useGetChatSettingsLazyQuery();
    const [emoji, setEmoji] = useState<ChatSettingsEmojiAction | undefined>(undefined);

    const updateCurrentEmoji = useCallback(() => {
        LoggingHandler.instance.wrap(async () => {
            const response = await getChatSettings({
                variables: { id: workflow?.triggers?.firstElement?.referenceId },
            });

            const currentEmoji = response.data?.chatSettings?.options?.slack?.emojiActions?.firstElement;

            setEmoji(currentEmoji);
        });
    }, [workflow, getChatSettings]);

    useEffect(() => {
        if (workflow?.triggers?.firstElement?.referenceId) {
            updateCurrentEmoji();
        }
    }, [workflow, updateCurrentEmoji]);

    const conversationSources = useMemo(
        () => workflow?.triggers?.map(trigger => ({
            label: trigger.text,
            value: trigger.referenceId,
        }))
        ?? [],
        [workflow],
    );

    const channelTags = useMemo(() => workflow?.triggers?.map(trigger => ({
        label: trigger.text,
        value: trigger.text,
    })) ?? [], [workflow]);

    const workspaceEventId = useMemo(() => workspaceEvents
        ?.find(event => event.workflowId === workflow?.id)?.eventTypeId as string ?? null, [workspaceEvents, workflow]);

    const emailTriggers = useMemo(() => workflow?.triggers?.filter(
        trigger => trigger.type === WorkflowTriggerType.Email,
    ).map(trigger => trigger.referenceId) ?? [], [workflow]);

    const { tracker, properties } = useFormBuilder<unknown, PropertiesByTrigger<T>>(
        builder => {
            let workingBuilder = builder
                .property("name", () => workflow?.name ?? "")
                .property("description", () => workflow?.description ?? "");

            switch (triggerType) {
            case WorkflowTriggerType.Action:
                workingBuilder = workingBuilder
                    .property("groups", () => formData?.userGroupIds ?? [])
                    .property(
                        "startCondition",
                        () => formData?.actionSettings?.startCondition
                            ?? ActionExecuteStartCondition.AfterStarted,
                    );
                break;

            case WorkflowTriggerType.InboundWebhook:
                workingBuilder = workingBuilder
                    .property("groups", () => formData?.userGroupIds ?? []);
                break;
            case WorkflowTriggerType.Emoji:
                workingBuilder = workingBuilder
                    .property("emoji", () => emoji?.emoji)
                    .property("groups", () => emoji?.allowedExecuteGroups ?? [EVERYONE_GROUP])
                    .property("conversationSources", () => conversationSources);
                break;

            case WorkflowTriggerType.ChannelTag:
                workingBuilder = workingBuilder
                    .property("channelTags", () => channelTags);
                break;
            case WorkflowTriggerType.Email:
                workingBuilder = workingBuilder
                    .property("emails", () => emailTriggers);
                break;
            case WorkflowTriggerType.WorkspaceAction:
                workingBuilder = workingBuilder
                    .property("event", () => workspaceEventId);
                break;
            case WorkflowTriggerType.WebChat:
            case WorkflowTriggerType.Integration:
            default:
                break;
            }

            return workingBuilder;
        },
        [formData, triggerType, workflow, workspaceEvents],
    );

    return { tracker, properties };
}
